import { useMemo } from 'react';

import { useCageController_getWelfareOverview } from '@/services/hooks';
import type { CageWelfareOverviewResponseDto } from '@/services/types';

import type { DomainQueryOptions } from '../shared/types';

import { mapCageWelfareOverviewToDomain } from './mappers';
import type { UseGetCageWelfareOverviewResult } from './types';

/**
 * The welfare card for the cage, or for the fish group named by `fishGroupId` — the cohort the
 * page is about (`fg`). Without one the request carries no query params, as it always did.
 */
export const useGetCageWelfareOverview = (
    cageId: number,
    params: { fishGroupId?: number } = {},
    options?: DomainQueryOptions<CageWelfareOverviewResponseDto>
): UseGetCageWelfareOverviewResult => {
    const query = useCageController_getWelfareOverview(
        cageId,
        params.fishGroupId != null ? { fishGroupId: params.fishGroupId } : undefined,
        options
    );

    const cageWelfareOverview = useMemo(() => {
        if (!query.data?.data) return null;
        return mapCageWelfareOverviewToDomain(query.data.data);
    }, [query.data?.data]);

    return {
        data: cageWelfareOverview,
        isLoading: query.isPending,
        isFetching: query.isFetching,
        isFetched: query.isFetched,
        error: query.error,
        refetch: query.refetch,
    };
};
